import React from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import {
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Mic,
  MicOff,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Collapsible Emergency Chat panel rendered inside the GlobalAlertOverlay.
 * Lets responders coordinate during an active alert. Shows the most recent
 * 15 messages, has a VTT (voice-to-text) mic, and posts new messages
 * directly to `/api/messages` with `chat_type: emergency`.
 *
 * Pure presentational where possible — message state, recording state, and
 * the input value all live on the parent so the chat keeps a single source
 * of truth with the rest of the overlay.
 */
export const EmergencyChat = ({
  messages,
  user,
  chatExpanded,
  onToggleExpanded,
  chatScrollRef,
  chatEndRef,
  chatInputRef,
  handleOverlayScroll,
  newMessage,
  setNewMessage,
  isRecording,
  startVoiceRecording,
  stopVoiceRecording,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    axios
      .post(`${API_URL}/messages`, {
        content: `[ALERT] ${newMessage}`,
        encrypted: false,
        chat_type: 'emergency',
      })
      .then(() => {
        setNewMessage('');
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      })
      .catch(() => {
        toast.error('Failed to send');
      });
  };

  return (
    <div
      className="border-2 border-primary rounded-sm mt-2 overflow-hidden"
      style={{
        boxShadow: '0 0 20px rgba(220, 38, 38, 0.3)',
        animation: 'chatPulse 2s ease-in-out infinite',
      }}
      onScroll={handleOverlayScroll}
      onWheel={handleOverlayScroll}
      onTouchMove={handleOverlayScroll}
      data-testid="emergency-chat"
    >
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between p-3 bg-primary/20 hover:bg-primary/30 transition-colors"
        data-testid="emergency-chat-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            <MessageSquare className="w-6 h-6 text-primary" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full" />
          </div>
          <span className="font-chivo font-bold text-sm uppercase text-white tracking-wide">
            🚨 EMERGENCY CHAT
          </span>
          {messages.length > 0 && (
            <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full font-bold">
              {messages.length}
            </span>
          )}
        </div>
        {chatExpanded ? (
          <ChevronUp className="w-5 h-5 text-white" />
        ) : (
          <ChevronDown className="w-5 h-5 text-white" />
        )}
      </button>

      {chatExpanded && (
        <div className="bg-black/60" onScroll={(e) => e.stopPropagation()}>
          <ScrollArea
            className="h-48 sm:h-56 p-3"
            ref={chatScrollRef}
            onWheel={(e) => e.stopPropagation()}
          >
            <div className="space-y-3">
              {messages.length === 0 ? (
                <div className="text-center py-4">
                  <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                  <p className="font-mono text-sm text-muted-foreground">No messages yet</p>
                  <p className="font-mono text-xs text-muted-foreground">
                    Send a message to coordinate
                  </p>
                </div>
              ) : (
                messages.slice(-15).map((msg) => {
                  const msgTime = msg.created_at ? new Date(msg.created_at) : new Date();
                  const timeStr = msgTime.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true,
                  });
                  const dateStr = msgTime.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                  });
                  const own = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={own ? 'text-right' : 'text-left'}>
                      <div
                        className="flex items-center gap-2 flex-wrap"
                        style={{ justifyContent: own ? 'flex-end' : 'flex-start' }}
                      >
                        <span className="font-mono text-primary text-xs font-bold">
                          {msg.sender_name}
                        </span>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {timeStr} • {dateStr}
                        </span>
                      </div>
                      <p className="text-white font-inter text-sm mt-0.5 leading-relaxed">
                        {msg.content}
                      </p>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          <form
            onSubmit={handleSubmit}
            className="flex gap-2 p-3 border-t border-white/20 items-center bg-black/40"
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (isRecording) {
                  stopVoiceRecording();
                } else {
                  startVoiceRecording();
                }
              }}
              className={`h-[70px] w-[70px] rounded-full shrink-0 flex items-center justify-center transition-colors select-none ${
                isRecording
                  ? 'bg-red-600 text-white shadow-lg shadow-red-500/50'
                  : 'bg-green-600 text-white hover:bg-green-500 shadow-md shadow-green-500/30'
              }`}
              style={{
                WebkitUserSelect: 'none',
                userSelect: 'none',
                WebkitTouchCallout: 'none',
                touchAction: 'manipulation',
              }}
              title={isRecording ? 'Tap to stop' : 'Tap to speak'}
              data-testid="overlay-vtt-button"
            >
              {isRecording ? (
                <MicOff className="w-8 h-8 animate-pulse" />
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </button>
            <Input
              ref={chatInputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onFocus={() => {
                setTimeout(() => {
                  chatInputRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }, 300);
              }}
              placeholder={
                isRecording ? '🎤 Listening... Tap mic to stop' : 'Type emergency message...'
              }
              className="bg-black/50 border-white/30 rounded-full font-inter text-sm h-12 flex-1 px-4"
              data-testid="emergency-chat-input"
            />
            <Button
              type="submit"
              className="h-12 w-12 bg-primary rounded-full shrink-0"
              data-testid="emergency-chat-send"
            >
              <Send className="w-5 h-5" />
            </Button>
          </form>
        </div>
      )}
    </div>
  );
};

export default EmergencyChat;
